/**
 * Request Guarantor (v1:8306-8606 semantics, Send-Link visual language).
 *
 * When the applicant provided guarantor contact details during onboarding
 * (property step `guarantor`, with an email), the dialog shows a read-only
 * "Guarantor info found from onboarding" card and a single "Send Guarantor
 * Request" — one click sends the invitation with that info. Otherwise it falls
 * back to the two-mode form: enter contact details, or ask the tenant to add a
 * guarantor. Phone/relationship are display-only — v1 reads but discards them
 * server-side (recorded quirk; the v2 schema ignores them).
 *
 * Pre-screen confirm (Figma): single vs multi applicant copy, optional wallet
 * shortfall banner + Top up, then Send. Upgrade still happens on guarantor
 * submit — request only flips guarantorRequired + invite.
 *
 * Posts `{ intent: 'requestGuarantor', applicationId, guarantorEmail?,
 * guarantorFirstName?, guarantorLastName? }`.
 */
import {
	ArrowRight,
	Check,
	ChevronDown,
	CircleAlert,
	Search,
	X,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useFetcher } from 'react-router'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.js'
import { RadioGroup, RadioGroupItem } from '#app/components/ui/radio-group'
import { deriveProvidedGuarantor } from '#app/utils/application-detail-v2/display'
import {
	countGuarantorUpgradeApplicants,
	deriveGuarantorUpgradeConfirm,
	formatGuarantorConfirmAddress,
} from '#app/utils/application-detail-v2/guarantor-upgrade-confirm'
import type {
	ApplicationSummary,
	DetailActionResult,
	GroupMember,
	ParsedStepData,
	PropertySummary,
} from '#app/utils/application-detail-v2/types'
import { cn } from '#app/utils/misc'
import { RELATIONSHIP_VALUES } from '#app/utils/relationship-options'
import { PastelChip } from '../primitives'
import {
	ActionResultMarker,
	DetailDialog,
	FloatingField,
	InlineActionError,
	PillButton,
} from './dialog-bits'

type RequestMode = 'has-details' | 'request-tenant'

/** Pragmatic email format check (same shape used across the app's forms). */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Read-only label + value cell for the onboarding pre-fill card. */
function ReadField({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-xl border border-[#F0F0F0] px-3 py-2">
			<p className="text-sm text-[#747474]">{label}</p>
			<p className="text-[15px] font-medium text-[#32302F]">{value || '-'}</p>
		</div>
	)
}

/** Two-letter initials (first + last word) for the member avatar. */
function memberInitials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean)
	if (parts.length === 0) return '?'
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Green-ringed initials avatar with the design's check badge (member picker). */
function MemberAvatar({ name }: { name: string }) {
	return (
		<span className="relative inline-flex size-12 shrink-0 items-center justify-center rounded-full border border-[#16A34A] bg-[#F0FDF4] text-[15px] font-semibold text-[#16A34A]">
			{memberInitials(name)}
			<span className="absolute -top-0.5 -right-0.5 inline-flex size-4 items-center justify-center rounded-full bg-[#16A34A] ring-2 ring-white">
				<Check className="size-2.5 text-white" />
			</span>
		</span>
	)
}

/** One member row in the picker + the selected-member summary card. */
function MemberCard({
	member,
	onRemove,
}: {
	member: GroupMember
	/** When set, renders the X to clear the selection (summary card); omitted in
	 * the selectable list rows. */
	onRemove?: () => void
}) {
	return (
		<div className="flex items-center gap-3 rounded-2xl border border-[#F0F0F0] px-5 py-4">
			<MemberAvatar name={member.displayName} />
			<span className="min-w-0 flex-1">
				<span className="block truncate text-[17px] font-semibold text-[#32302F]">
					{member.displayName}
				</span>
				{member.email ? (
					<span className="block truncate text-[15px] text-[#747474]">
						{member.email}
					</span>
				) : null}
			</span>
			{onRemove ? (
				<button
					type="button"
					onClick={onRemove}
					aria-label="Change tenant"
					className="shrink-0 text-[#747474] transition-colors hover:text-[#32302F]"
				>
					<X className="size-5" />
				</button>
			) : (
				<PastelChip
					tone="neutral"
					label={member.relationshipLabel}
					className="shrink-0 px-3 py-1 text-[12px]"
				/>
			)}
		</div>
	)
}

export function RequestGuarantorDialog({
	open,
	onOpenChange,
	application,
	propertyStep,
	property,
	members,
	/** Household members for confirm copy only (does NOT enable the picker). */
	upgradeHouseholdMembers,
	/** Individual page: true when the target app is pre-screen (upgrade warn). */
	willUpgradeOnGuarantorComplete = false,
	/**
	 * When false (student_housing), skip pre→full confirm + shortfall — guarantor
	 * request does not upgrade the household. Default true (standard apps).
	 */
	guarantorUpgradeEnabled = true,
	/** Viewer wallet balance — shortfall banner when below the upgrade estimate. */
	walletBalance = null,
	/** Per-applicant pre→full credit estimate (null hides shortfall math). */
	upgradeCreditsPerApplicant = null,
	/** Opens RenewCredits without closing this dialog (pending send preserved). */
	onTopUp,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	application: ApplicationSummary
	/** Drives the "found from onboarding" pre-fill (property step `guarantor`). */
	propertyStep?: ParsedStepData['property']
	/** Confirm subtitle address ("2100 Camilla Road, Unit 20"). */
	property?: Pick<PropertySummary, 'address' | 'unitNumber'>
	/** Combined page: household members. When present, the dialog first asks WHICH
	 * member the guarantor is for, then posts that member's applicationId. */
	members?: GroupMember[]
	/** Individual household page: group members used only to count who upgrades. */
	upgradeHouseholdMembers?: GroupMember[]
	willUpgradeOnGuarantorComplete?: boolean
	guarantorUpgradeEnabled?: boolean
	walletBalance?: number | null
	upgradeCreditsPerApplicant?: number | null
	onTopUp?: () => void
}) {
	const fetcher = useFetcher<DetailActionResult>()
	const [mode, setMode] = useState<RequestMode>('request-tenant')
	const [firstName, setFirstName] = useState('')
	const [lastName, setLastName] = useState('')
	const [email, setEmail] = useState('')
	const [relation, setRelation] = useState('')
	const [phone, setPhone] = useState('')
	// Client-side validation messages for the "enter details directly" form
	// (relationship/phone are optional — discarded server-side).
	const [errors, setErrors] = useState<{
		firstName?: string
		lastName?: string
		email?: string
	}>({})
	// Combined page only: which household member the guarantor is for (null until
	// picked) + the picker's search query.
	const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null)
	const [memberSearch, setMemberSearch] = useState('')
	/** Pre-screen confirm: hold the pending send until the landlord confirms. */
	const [pendingSend, setPendingSend] = useState<{
		guarantorEmail: string
		guarantorFirstName: string
		guarantorLastName: string
	} | null>(null)
	const [pendingAskTenant, setPendingAskTenant] = useState(false)
	const busy = fetcher.state !== 'idle'

	// Members eligible to receive a guarantor (a guarantor doesn't get its own).
	const pickableMembers = (members ?? []).filter(
		(m) => m.applicationType !== 'guarantor',
	)
	const hasMemberPicker = pickableMembers.length > 0
	const selectedMember =
		pickableMembers.find((m) => m.applicationId === selectedMemberId) ?? null
	const filteredMembers = (() => {
		const q = memberSearch.trim().toLowerCase()
		if (!q) return pickableMembers
		return pickableMembers.filter(
			(m) =>
				m.displayName.toLowerCase().includes(q) ||
				(m.email ?? '').toLowerCase().includes(q),
		)
	})()
	// The request targets the picked member on the combined page, else the page's
	// own application (individual page).
	const targetApplicationId = selectedMember?.applicationId ?? application.id

	// Onboarding pre-fill is an individual-page nicety (the property step belongs
	// to one applicant); the combined picker always uses the manual form.
	const provided = hasMemberPicker
		? null
		: deriveProvidedGuarantor(propertyStep?.guarantor)
	// Onboarding pre-fill needs an email to send the invitation; without one,
	// fall back to the manual form so the landlord can supply it.
	const fromOnboarding = Boolean(provided?.email)

	const send = (fields: {
		guarantorEmail: string
		guarantorFirstName: string
		guarantorLastName: string
	}) => {
		void fetcher.submit(
			{
				intent: 'requestGuarantor',
				applicationId: targetApplicationId,
				...fields,
			},
			{ method: 'post' },
		)
	}

	// Confirm when ANY still-pre-screen non-guarantor will upgrade on submit —
	// not only when the invite target is still pre-screen. A full-screened
	// Applicant 1 can request a guarantor while Applicant 2 is still pre.
	const upgradeCountMembers = members ?? upgradeHouseholdMembers ?? []
	const upgradeApplicantCount = countGuarantorUpgradeApplicants({
		members: upgradeCountMembers,
		soloWillUpgrade: willUpgradeOnGuarantorComplete,
		upgradeEnabled: guarantorUpgradeEnabled,
	})
	const needsUpgradeConfirm = upgradeApplicantCount > 0

	const requestSend = (fields: {
		guarantorEmail: string
		guarantorFirstName: string
		guarantorLastName: string
	}) => {
		if (needsUpgradeConfirm) {
			setPendingSend(fields)
			return
		}
		send(fields)
	}

	const requestAskTenant = () => {
		if (needsUpgradeConfirm) {
			setPendingAskTenant(true)
			return
		}
		void fetcher.submit(
			{ intent: 'requestGuarantor', applicationId: targetApplicationId },
			{ method: 'post' },
		)
	}

	const confirmPending = () => {
		if (pendingSend) {
			send(pendingSend)
			setPendingSend(null)
			return
		}
		if (pendingAskTenant) {
			void fetcher.submit(
				{ intent: 'requestGuarantor', applicationId: targetApplicationId },
				{ method: 'post' },
			)
			setPendingAskTenant(false)
		}
	}

	const submitManual = () => {
		if (mode === 'has-details') {
			// Required: first name, last name, a valid email. Relationship/phone are
			// optional (the server discards them).
			const trimmedEmail = email.trim()
			const nextErrors: typeof errors = {}
			if (!firstName.trim()) nextErrors.firstName = 'First name is required'
			if (!lastName.trim()) nextErrors.lastName = 'Last name is required'
			if (!trimmedEmail) {
				nextErrors.email = 'Email is required'
			} else if (!EMAIL_RE.test(trimmedEmail)) {
				nextErrors.email = 'Enter a valid email address'
			}
			if (Object.keys(nextErrors).length > 0) {
				setErrors(nextErrors)
				return
			}
			setErrors({})
			requestSend({
				guarantorEmail: trimmedEmail,
				guarantorFirstName: firstName.trim(),
				guarantorLastName: lastName.trim(),
			})
			return
		}
		// "Ask the tenant" — no email; the handler emails the applicant instead.
		requestAskTenant()
	}

	// Reset the member picker (and search) whenever the dialog closes so a fresh
	// open starts at the member-selection step.
	const handleOpenChange = (next: boolean) => {
		if (!next) {
			setSelectedMemberId(null)
			setMemberSearch('')
			setErrors({})
			setPendingSend(null)
			setPendingAskTenant(false)
		}
		onOpenChange(next)
	}

	const alreadyRequested = application.guarantorRequired ? (
		<p className="w-fit rounded-full bg-amber-50 px-4 py-1.5 text-sm font-medium text-amber-600">
			Already Requested
		</p>
	) : null

	// Shared canonical list — same options as the tenant screening PropertyStep
	// relationship fields (keeps the invite dropdown consistent across surfaces).
	const relationships = RELATIONSHIP_VALUES

	const showingUpgradeConfirm = pendingSend !== null || pendingAskTenant

	const singleUpgradeName =
		upgradeCountMembers.find(
			(m) =>
				m.applicationType !== 'guarantor' &&
				m.screeningKind === 'pre_screening',
		)?.displayName ??
		selectedMember?.displayName ??
		application.applicantName
	const upgradeConfirm = deriveGuarantorUpgradeConfirm({
		applicantName: singleUpgradeName,
		upgradeApplicantCount,
		walletBalance,
		upgradeCreditsPerApplicant,
	})
	const confirmAddress = property
		? formatGuarantorConfirmAddress(property)
		: null

	let body: ReactNode
	if (showingUpgradeConfirm) {
		const {
			subject,
			isMulti,
			creditsShort,
			upgradeApplicantCount: n,
		} = upgradeConfirm
		body = (
			<div className="flex flex-col gap-8">
				<div className="flex flex-col gap-4">
					<p className="text-left text-[15px] leading-[1.55] text-[#32302F]">
						<span className="font-semibold">{subject}</span>
						{isMulti
							? creditsShort != null
								? ' will move from Pre-Screen to Full Screen once the guarantor submits. '
								: ' will move from Pre-Screen to Full Screen. '
							: ' will move from Pre-Screen to Full Screen once the guarantor submits. '}
						Guarantor reports can only be generated on Full Screen applicants.
					</p>
					{creditsShort != null ? (
						<div
							role="alert"
							className="flex gap-3 rounded-2xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3.5 text-left"
						>
							<CircleAlert
								className="mt-0.5 size-5 shrink-0 text-[#DC2626]"
								aria-hidden
							/>
							<div className="min-w-0 text-[15px] leading-relaxed text-[#DC2626]">
								<p className="font-semibold">
									{isMulti
										? `You are ${creditsShort} credits short if all ${n} complete.`
										: `You are ${creditsShort} credits short.`}
								</p>
								<p className="mt-0.5 text-[#32302F]">
									{isMulti
										? 'Send the request now. Each Full Screen runs when the guarantor submits and your balance covers it.'
										: 'Send the request now. The Full Screen runs when the guarantor submits and your balance covers it.'}
								</p>
							</div>
						</div>
					) : null}
				</div>
				<div className="flex flex-wrap items-center justify-end gap-3">
					{creditsShort != null && onTopUp ? (
						<PillButton tone="outline" onClick={onTopUp} disabled={busy}>
							Top up credits
						</PillButton>
					) : null}
					<PillButton
						tone="outline"
						onClick={() => {
							setPendingSend(null)
							setPendingAskTenant(false)
						}}
						disabled={busy}
					>
						Cancel
					</PillButton>
					<PillButton tone="dark" disabled={busy} onClick={confirmPending}>
						{busy ? 'Processing...' : 'Send Guarantor Request'}
						<ArrowRight className="size-4" />
					</PillButton>
				</div>
			</div>
		)
	} else if (hasMemberPicker && !selectedMember) {
		// Combined page: choose which household member the guarantor is for first.
		body = (
			<>
				<p className="text-[15px] text-[#747474]">
					Select a tenant to request guarantor information for
				</p>
				<FloatingField
					label="Search Tenant By Name Or Email"
					htmlFor="guarantor-member-search"
					className="mt-1"
				>
					<div className="flex items-center gap-2">
						<Search className="size-4 shrink-0 text-[#747474]" />
						<input
							id="guarantor-member-search"
							value={memberSearch}
							onChange={(event) => setMemberSearch(event.target.value)}
							placeholder="Search Here..."
							className="w-full bg-transparent text-[15px] text-[#32302F] outline-none placeholder:text-[#CCCCCC]"
						/>
					</div>
				</FloatingField>
				<div className="flex max-h-80 flex-col gap-3 overflow-y-auto [scrollbar-width:thin]">
					{filteredMembers.length > 0 ? (
						filteredMembers.map((member) => (
							<button
								key={member.applicationId}
								type="button"
								onClick={() => setSelectedMemberId(member.applicationId)}
								className="rounded-2xl text-left transition-opacity hover:opacity-80"
							>
								<MemberCard member={member} />
							</button>
						))
					) : (
						<p className="py-6 text-center text-sm text-[#747474]">
							No tenants match your search.
						</p>
					)}
				</div>
			</>
		)
	} else if (fromOnboarding && provided && !application.guarantorRequired) {
		// The one-click onboarding card is for the FIRST request only. Once a
		// guarantor has been requested, "Request Another Guarantor" must offer the
		// manual form (ask the tenant, or enter a different guarantor's details) so
		// the landlord can request a DIFFERENT guarantor — even when onboarding
		// info exists (TT2: request-another-guarantor).
		body = (
			<>
				{alreadyRequested}
				<div className="rounded-2xl border border-[#F0F0F0] p-5">
					<h3 className="text-[17px] font-semibold text-[#32302F]">
						Guarantor info found from onboarding
					</h3>
					<div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
						<ReadField label="First Name" value={provided.firstName} />
						<ReadField label="Last Name" value={provided.lastName} />
						<ReadField label="Relationship" value={provided.relationship} />
						<ReadField label="Email" value={provided.email} />
						<ReadField label="Phone" value={provided.phone} />
					</div>
				</div>
				<p className="text-sm text-[#747474]">
					An email will be sent to the guarantor with a link to complete their
					verification and provide the required documents.
				</p>
				<InlineActionError fetcher={fetcher} open={open} />
				<div className="flex justify-end gap-3">
					<PillButton
						tone="outline"
						onClick={() => handleOpenChange(false)}
						disabled={busy}
					>
						Cancel
					</PillButton>
					<PillButton
						tone="dark"
						disabled={busy}
						onClick={() =>
							requestSend({
								guarantorEmail: provided.email,
								guarantorFirstName: provided.firstName,
								guarantorLastName: provided.lastName,
							})
						}
					>
						{busy ? 'Processing...' : 'Send Guarantor Request'}
						<ArrowRight className="size-4" />
					</PillButton>
				</div>
			</>
		)
	} else {
		body = (
			<>
				{alreadyRequested}
				{hasMemberPicker && selectedMember ? (
					<MemberCard
						member={selectedMember}
						onRemove={() => setSelectedMemberId(null)}
					/>
				) : null}
				<RadioGroup
					value={mode}
					onValueChange={(value) => {
						setMode(value as RequestMode)
						setErrors({})
					}}
					className="gap-3"
				>
					<label
						htmlFor="guarantor-mode-request-tenant"
						className={cn(
							'flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors',
							mode === 'request-tenant'
								? 'border-[#32302F]'
								: 'border-[#E2E2E0] hover:border-[#CCCCCC]',
						)}
					>
						<RadioGroupItem
							value="request-tenant"
							id="guarantor-mode-request-tenant"
							className="mt-0.5"
						/>
						<span>
							<span className="block font-medium text-[#32302F]">
								Ask Tenant to Provide Guarantor
							</span>
						</span>
					</label>
					<label
						htmlFor="guarantor-mode-has-details"
						className={cn(
							'cursor-pointer rounded-xl border p-4 transition-colors',
							mode === 'has-details'
								? 'border-[#32302F]'
								: 'border-[#E2E2E0] hover:border-[#CCCCCC]',
						)}
					>
						<div className="flex items-start gap-3">
							<RadioGroupItem
								value="has-details"
								id="guarantor-mode-has-details"
								className="mt-0.5"
							/>
							<span>
								<span className="block font-medium text-[#32302F]">
									Enter Guarantor’s Details Directly
								</span>
							</span>
						</div>
						{mode === 'has-details' ? (
							<div className="mt-5 flex flex-col gap-4 pt-1">
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<FloatingField
										label="First Name"
										htmlFor="guarantor-first-name"
										error={errors.firstName}
									>
										<input
											id="guarantor-first-name"
											value={firstName}
											onChange={(event) => {
												setFirstName(event.target.value)
												setErrors((prev) => ({ ...prev, firstName: undefined }))
											}}
											disabled={busy}
											placeholder="Enter First Name"
											className="w-full bg-transparent text-[15px] text-[#32302F] outline-none placeholder:text-[#CCCCCC]"
										/>
									</FloatingField>
									<FloatingField
										label="Last Name"
										htmlFor="guarantor-last-name"
										error={errors.lastName}
									>
										<input
											id="guarantor-last-name"
											value={lastName}
											onChange={(event) => {
												setLastName(event.target.value)
												setErrors((prev) => ({ ...prev, lastName: undefined }))
											}}
											disabled={busy}
											placeholder="Enter Last Name"
											className="w-full bg-transparent text-[15px] text-[#32302F] outline-none placeholder:text-[#CCCCCC]"
										/>
									</FloatingField>
								</div>
								<div className="mt-2">
									<FloatingField
										label="Relationship"
										htmlFor="guarantor-relationship"
									>
										<DropdownMenu>
											<DropdownMenuTrigger asChild disabled={busy}>
												<div
													role="button"
													tabIndex={0}
													aria-label="Select relationship"
													className="flex w-full cursor-pointer items-center gap-2 outline-none"
												>
													<span
														className={cn(
															'text-[15px]',
															relation ? 'text-[#32302F]' : 'text-[#CCCCCC]',
														)}
													>
														{relation || 'Select Relationship With You'}
													</span>

													<ChevronDown className="ml-auto size-4 shrink-0 text-gray-500" />
												</div>
											</DropdownMenuTrigger>
											<DropdownMenuContent
												align="start"
												sideOffset={12}
												className="w-[var(--radix-dropdown-menu-trigger-width)] rounded-2xl border-[#ECECEA] bg-white p-2 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.18)]"
											>
												{relationships.map((item) => (
													<DropdownMenuItem
														key={item}
														onSelect={() => setRelation(item)}
														className="flex cursor-pointer items-center justify-between gap-3 rounded-xl px-2 py-2 focus:bg-[#F6F7F5]"
													>
														<span className="font-Regular truncate text-[15px] text-[#32302F]">
															{item}
														</span>

														{relation === item ? (
															<Check className="size-4 text-[#16A34A]" />
														) : null}
													</DropdownMenuItem>
												))}
											</DropdownMenuContent>
										</DropdownMenu>
									</FloatingField>
								</div>
								<div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
									<FloatingField
										label="Email"
										htmlFor="guarantor-email"
										error={errors.email}
									>
										<input
											id="guarantor-email"
											type="email"
											value={email}
											onChange={(event) => {
												setEmail(event.target.value)
												setErrors((prev) => ({ ...prev, email: undefined }))
											}}
											disabled={busy}
											placeholder="Enter Email Address"
											className="w-full bg-transparent text-[15px] text-[#32302F] outline-none placeholder:text-[#CCCCCC]"
										/>
									</FloatingField>
									<FloatingField label="Phone Number" htmlFor="guarantor-phone">
										<input
											id="guarantor-phone"
											type="tel"
											value={phone}
											onChange={(event) => setPhone(event.target.value)}
											disabled={busy}
											placeholder="Enter Phone Number"
											className="w-full bg-transparent text-[15px] text-[#32302F] outline-none placeholder:text-[#CCCCCC]"
										/>
									</FloatingField>
								</div>
							</div>
						) : null}
					</label>
				</RadioGroup>

				<InlineActionError fetcher={fetcher} open={open} />

				<div className="flex justify-end gap-3">
					<PillButton
						tone="outline"
						onClick={() => handleOpenChange(false)}
						disabled={busy}
					>
						Cancel
					</PillButton>
					<PillButton tone="dark" disabled={busy} onClick={submitManual}>
						{busy
							? 'Processing...'
							: mode === 'has-details'
								? 'Add & Send Guarantor Request'
								: 'Send Guarantor Request'}
						<ArrowRight className="size-4" />
					</PillButton>
				</div>
			</>
		)
	}

	return (
		<>
			<ActionResultMarker
				fetcher={fetcher}
				fallback="Guarantor request sent"
				onSuccess={() => handleOpenChange(false)}
			/>
			<DetailDialog
				open={open}
				onOpenChange={handleOpenChange}
				title="Request Guarantor"
				description={
					showingUpgradeConfirm && confirmAddress ? (
						confirmAddress
					) : (
						<>
							For Tenant{' '}
							<span className="text-[#32302F]">
								{application.applicantName}
							</span>
						</>
					)
				}
				className={
					showingUpgradeConfirm
						? 'max-h-[90vh] overflow-y-auto [scrollbar-width:thin] sm:max-w-[560px]'
						: 'max-h-[90vh] overflow-y-auto [scrollbar-width:thin] sm:max-w-xl'
				}
			>
				{body}
			</DetailDialog>
		</>
	)
}
